import { useMutation, useQueryClient, QueryKey } from '@tanstack/react-query';
import { PermanentJobsService } from '../services';
import { ICandidateProfessionalEntity, IGetCandidateListResult } from '../types';
import { showErrorMessage } from '~/domain/utils';

interface IUseToggleShortlistCandidate {
  candidateId: string;
  isShortlisted: boolean;
}

export const useToggleShortlistCandidate = ({
  candidateId,
  isShortlisted,
}: IUseToggleShortlistCandidate) => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    void,
    {
      previousCandidateListData: Array<[QueryKey, IGetCandidateListResult | undefined]>;
      previousCandidateProfessionalData: Array<
        [QueryKey, ICandidateProfessionalEntity | undefined]
      >;
    }
  >({
    mutationFn: () => {
      if (isShortlisted) {
        return PermanentJobsService.deleteShortlistCandidate({
          candidateId,
        });
      }

      return PermanentJobsService.createShortlistCandidate({ candidateId });
    },
    onMutate: async () => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['GET_CANDIDATE_LIST'] });
      await queryClient.cancelQueries({ queryKey: ['GET_CANDIDATE_PROFESSIONAL'] });

      const previousCandidateListData = queryClient.getQueriesData<IGetCandidateListResult>({
        queryKey: ['GET_CANDIDATE_LIST'],
      });

      const previousCandidateProfessionalData =
        queryClient.getQueriesData<ICandidateProfessionalEntity>({
          queryKey: ['GET_CANDIDATE_PROFESSIONAL'],
        });

      const newIsShortlistedValue = !isShortlisted;

      // Optimistically update GET_CANDIDATE_LIST
      queryClient.setQueriesData<IGetCandidateListResult>(
        { queryKey: ['GET_CANDIDATE_LIST'] },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            data: oldData.data.map((candidate) =>
              candidate.id === candidateId
                ? { ...candidate, isShortlisted: newIsShortlistedValue }
                : candidate,
            ),
          };
        },
      );

      // Optimistically update GET_CANDIDATE_PROFESSIONAL
      queryClient.setQueriesData<ICandidateProfessionalEntity>(
        { queryKey: ['GET_CANDIDATE_PROFESSIONAL'] },
        (oldData) => {
          if (!oldData || oldData.id !== candidateId) return oldData;

          return {
            ...oldData,
            isShortlisted: newIsShortlistedValue,
          };
        },
      );

      return { previousCandidateListData, previousCandidateProfessionalData };
    },
    onError(error, _variables, context) {
      if (context?.previousCandidateListData) {
        context.previousCandidateListData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      if (context?.previousCandidateProfessionalData) {
        context.previousCandidateProfessionalData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      showErrorMessage({ error });
    },
  });
};
